import {Injectable, inject, Inject} from '@angular/core';
import {BehaviorSubject, Observable, Subject, firstValueFrom} from 'rxjs';
import {RequestService} from '@services/http/request/request.service';
import {APP_CONFIG, AppConfig} from '@src/app.config';
import {WsClientFrame, WsEnvelope} from '../models/websocket.models';

@Injectable({providedIn: 'root'})
export class WebsocketService {
  private readonly TAG = '[WebsocketService]';

  private readonly appCfg = inject(APP_CONFIG);
  private readonly req = inject(RequestService);

  private readonly wsBaseUrl: string;
  private readonly issueTokenUrl: string;

  private ws?: WebSocket;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10; // ⬅️ NEW: max number of retries
  private readonly maxReconnectDelay = 30_000;
  private reconnectTimer?: any;
  private manualClose = false;

  private messagesSubject = new Subject<WsEnvelope>();
  readonly messages$: Observable<WsEnvelope> = this.messagesSubject.asObservable();

  private statusSubject = new BehaviorSubject<number>(WebSocket.CLOSED);
  readonly status$ = this.statusSubject.asObservable();

  private issuedToken?: string;

  // ⬇️ NEW: keep desired topics + queued frames while disconnected
  private desiredTopics = new Set<string>();
  private outbox: WsClientFrame[] = [];

  constructor() {
    const wsConfig = this.appCfg.wsConfig

    this.wsBaseUrl = `${wsConfig.websocketProtocol}://${this.appCfg.websocketBaseDomain}`
    this.issueTokenUrl = `${wsConfig.websocketHttpProtocol}://${this.appCfg.websocketBaseDomain}/api/issue-token`
  }

  get status(): number {
    return this.statusSubject.getValue();
  }

  // ───────── token
  async initToken(): Promise<void> {
    try {
      const res = await firstValueFrom(this.req.post(this.issueTokenUrl));
      this.issuedToken = res?.body?.token ?? null;
    } catch (err) {
      console.error('Failed to issue WS token', err);
      this.issuedToken = undefined;
    }
  }

  // ───────── connect / close
  async connect(): Promise<void> {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.manualClose = false;
    if (!this.issuedToken) await this.initToken();
    if (!this.issuedToken) {
      console.error(`${this.TAG} connect aborted: unable to issue websocket token`);
      this.statusSubject.next(WebSocket.CLOSED);
      return;
    }
    this.openSocket();
  }

  close(code?: number, reason?: string): void {
    const activeTopics = Array.from(this.desiredTopics);
    if (this.appCfg.environment === 'development') {
      console.info(`${this.TAG} close requested`, {
        code: code ?? 1000,
        reason: reason ?? 'manual-close',
        hadSocket: !!this.ws,
        readyState: this.ws?.readyState,
        desiredTopics: activeTopics,
      });
    }
    this.manualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.ws?.close(code, reason);
    this.ws = undefined;
    this.statusSubject.next(WebSocket.CLOSED);
  }

  isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // ───────── client frames
  private sendFrame<T = unknown>(frame: WsClientFrame<T>): void {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      // Queue while disconnected; de-dupe joins
      if (frame.type === 'join') {
        // only keep one pending join per topic
        const alreadyQueued = this.outbox.some(f => f.type === 'join' && f.topic === frame.topic);
        if (!alreadyQueued) this.outbox.push(frame);
      } else {
        this.outbox.push(frame);
      }
      if (this.appCfg.environment === 'development') {
        console.warn(`${this.TAG} queued frame (socket not open):`, frame);
      }
      return;
    }
    this.ws.send(JSON.stringify(frame));
    if (this.appCfg.environment === 'development') console.log(this.TAG, '→', frame);
  }

  joinTopic(topic: string): void {
    if (!topic) return;
    this.desiredTopics.add(topic);
    this.sendFrame({type: 'join', topic});
  }

  leaveTopic(topic: string): void {
    if (!topic) return;
    this.desiredTopics.delete(topic);
    // Remove any pending join for this topic from outbox
    this.outbox = this.outbox.filter(f => !(f.type === 'join' && f.topic === topic));
    this.sendFrame({type: 'leave', topic});
    this.closeIfNoDesiredTopics();
  }

  sendEvent<T = unknown>(topic: string, body: T): void {
    this.sendFrame<T>({type: 'event', topic, body});
  }

  hasDesiredTopic(topicPrefix?: string): boolean {
    if (!topicPrefix) return this.desiredTopics.size > 0;
    for (const topic of this.desiredTopics) {
      if (topic.startsWith(topicPrefix)) return true;
    }
    return false;
  }

  hasDesiredTopics(): boolean {
    return this.desiredTopics.size > 0;
  }

  // ───────── internals
  private openSocket(): void {
    const tokenParam = this.issuedToken ? `?token=${encodeURIComponent(this.issuedToken)}` : '';
    const url = `${this.wsBaseUrl}/ws/notify${tokenParam}`;

    this.ws = new WebSocket(url);
    this.statusSubject.next(WebSocket.CONNECTING);

    this.ws.onopen = () => {
      if (this.appCfg.environment === 'development') console.log(`${this.TAG} connection opened`);
      this.reconnectAttempts = 0;
      this.statusSubject.next(WebSocket.OPEN);

      // ⬇️ Rejoin all desired topics first
      for (const topic of this.desiredTopics) {
        this.ws!.send(JSON.stringify({type: 'join', topic}));
        if (this.appCfg.environment === 'development') console.log(this.TAG, '→ (rejoin)', topic);
      }

      this.flushFrameQueue()
    };

    this.ws.onmessage = (ev: MessageEvent) => {
      const raw = ev.data;
      try {
        const parsed = JSON.parse(raw);
        const envelope: WsEnvelope =
          parsed && typeof parsed === 'object'
            ? {
              meta: parsed.meta ?? undefined,
              target: parsed.target ?? {hub: '', topic: '', userIds: null},
              body: parsed.body ?? parsed,
            }
            : {body: parsed, target: {hub: '', topic: '', userIds: null}};

        if (this.appCfg.environment === 'development') console.log(this.TAG, 'msg ←', envelope);
        this.messagesSubject.next(envelope);
      } catch {
        const envelope: WsEnvelope<string> = {
          body: String(raw),
          target: {hub: '', topic: '', userIds: null},
        };
        if (this.appCfg.environment === 'development') console.log(this.TAG, 'msg ← (text)', envelope);
        this.messagesSubject.next(envelope);
      }
    };

    this.ws.onerror = (err) => {
      console.error('Error', err);
      this.ws?.close();

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error(`${this.TAG} reached max reconnect attempts (${this.maxReconnectAttempts}). Giving up.`);
        this.statusSubject.next(WebSocket.CLOSED);
        return;
      }

      console.log('There was a WS error, reloading token...');
      this.initToken()
        .then(_ => {
          if (!this.issuedToken) {
            console.error(`${this.TAG} reconnect aborted: unable to re-issue websocket token`);
            this.statusSubject.next(WebSocket.CLOSED);
            return;
          }
          this.openSocket();
        });
    };

    this.ws.onclose = (event) => {
      const closeReasons: Record<number, string> = {
        1000: 'Normal Closure',
        1001: 'Going Away',
        1002: 'Protocol Error',
        1003: 'Unsupported Data',
        1006: 'Abnormal Closure',
        1007: 'Invalid frame payload data',
        1008: 'Policy Violation',
        1009: 'Message Too Big',
        1010: 'Missing Extension',
        1011: 'Internal Error',
        1015: 'TLS Handshake',
      };

      console.warn(`${this.TAG} WS closed`, {
        code: event.code,
        reason: closeReasons[event.code] ?? event.reason,
        wasClean: event.wasClean,
      });
      if (this.manualClose) {
        console.info(`${this.TAG} disconnect confirmed`, {
          code: event.code,
          reason: closeReasons[event.code] ?? event.reason,
          wasClean: event.wasClean,
          remainingDesiredTopics: Array.from(this.desiredTopics),
        });
      }

      this.statusSubject.next(WebSocket.CLOSED);

      if (!this.manualClose) {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        } else {
          console.error(`${this.TAG} exceeded max reconnect attempts (${this.maxReconnectAttempts}). Manual restart required.`);
        }
      }
    };
  }

  private flushFrameQueue(): void {
    // ⬇️ Flush any queued frames in order (joins deduped above)
    if (this.outbox.length) {
      for (const frame of this.outbox) {
        // If a queued leave targets a topic no longer desired, still send it.
        // If a queued join exists, it’s harmless (we already rejoined).
        this.ws!.send(JSON.stringify(frame));
        if (this.appCfg.environment === 'development') console.log(this.TAG, '→ (flush)', frame);
      }
      this.outbox = [];
    }
  }

  private scheduleReconnect(): void {
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts++), this.maxReconnectDelay);
    this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
  }

  private closeIfNoDesiredTopics(): void {
    if (this.hasDesiredTopics()) return;
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      if (this.appCfg.environment === 'development') {
        console.info(`${this.TAG} no desired topics remain; closing socket`);
      }
      this.close(1000, 'No topics');
    }
  }
}
